import { useState } from 'react';
import { getThemeChoice, setThemeChoice, type ThemeChoice } from '@/lib/theme';
import { Icon } from '@/components/Icon';
import { DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@/components/ui/dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const OPTIONS: Array<{ value: ThemeChoice; label: string; icon: string }> = [
  { value: 'system', label: 'System', icon: 'settings' },
  { value: 'dark', label: 'Dark', icon: 'visibility_off' },
  { value: 'light', label: 'Light', icon: 'visibility' },
];

const isChoice = (v: string): v is ThemeChoice => v === 'system' || v === 'dark' || v === 'light';

export function useThemeChoice() {
  const [choice, setChoice] = useState<ThemeChoice>(getThemeChoice);
  const set = (v: string) => {
    if (!isChoice(v)) return;
    setThemeChoice(v);
    setChoice(v);
  };
  return [choice, set] as const;
}

/** Menu variant (desktop / rail user menu): a radio group that stays open while switching. */
export function ThemeMenuItems() {
  const [choice, set] = useThemeChoice();
  return (
    <>
      <DropdownMenuLabel>Theme</DropdownMenuLabel>
      <DropdownMenuRadioGroup value={choice} onValueChange={set}>
        {OPTIONS.map((o) => (
          <DropdownMenuRadioItem key={o.value} value={o.value} onSelect={(e) => e.preventDefault()}>
            <Icon name={o.icon} size={18} className="text-muted-foreground" />
            {o.label}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  );
}

/** Segmented variant (phone sheet): one row, whole. */
export function ThemeSegmented() {
  const [choice, set] = useThemeChoice();
  return (
    <ToggleGroup
      type="single"
      value={choice}
      onValueChange={(v) => v && set(v)}
      aria-label="Theme"
      spacing={1}
      className="grid w-full grid-cols-3 rounded-lg border bg-secondary p-1"
    >
      {OPTIONS.map((o) => (
        <ToggleGroupItem key={o.value} value={o.value} className="h-10 w-full">
          {o.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
